import { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export const MENU_TYPES = [
  { value: 'daily_meals', label: 'Daily Meals', description: 'Scheduled day-by-day meals' },
  { value: 'canteen', label: 'Canteen Items', description: 'Always available (drinks, snacks)' },
  { value: 'office_platters', label: 'Office Platters', description: 'Bulk orders for offices' }
];

const emptyItem = {
  name: '',
  description: '',
  price: '',
  category: '',
  menu_type: 'daily_meals',
  image_url: '',
  allergens: [],
  is_vegetarian: false,
  stock_quantity: 0,
  min_serving_size: 1,
  is_loyalty_eligible: true,
  has_sides: false,
  free_sides_count: 1,
  additional_side_price: 15,
  available_side_ids: []
};

const itemToForm = (item) => ({
  name: item.name,
  description: item.description,
  price: item.price.toString(),
  category: item.category,
  menu_type: item.menu_type || 'daily_meals',
  image_url: item.image_url || '',
  allergens: item.allergens || [],
  is_vegetarian: item.is_vegetarian || false,
  stock_quantity: item.stock_quantity || 0,
  min_serving_size: item.min_serving_size || 1,
  is_loyalty_eligible: item.is_loyalty_eligible !== false,
  has_sides: item.sides_config?.has_sides || false,
  free_sides_count: item.sides_config?.free_sides_count ?? 1,
  additional_side_price: item.sides_config?.additional_side_price || 15,
  available_side_ids: item.sides_config?.available_side_ids || []
});

export const MenuItemFormDialog = ({ open, onOpenChange, editingItem, availableSides, onSaved }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFormData(editingItem ? itemToForm(editingItem) : emptyItem);
      setImagePreview(editingItem?.image_url || null);
    }
  }, [open, editingItem]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, WEBP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed');
      return;
    }

    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await fetch(`${API_URL}/api/admin/menu/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: uploadFormData
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      const data = await response.json();
      const imageUrl = data.image_url.startsWith('http') ? data.image_url : `${API_URL}${data.image_url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.message);
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingItem
        ? `${API_URL}/api/admin/menu/${editingItem.item_id}`
        : `${API_URL}/api/admin/menu`;
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          stock_quantity: parseInt(formData.stock_quantity),
          allergens: typeof formData.allergens === 'string'
            ? formData.allergens.split(',').map(a => a.trim()).filter(Boolean)
            : formData.allergens
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save item');
      }

      const savedItem = await response.json();
      const savedItemId = savedItem.item_id || editingItem?.item_id;

      if (savedItemId && formData.menu_type === 'daily_meals') {
        await fetch(`${API_URL}/api/admin/menu/${savedItemId}/sides-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({
            has_sides: formData.has_sides,
            available_side_ids: formData.has_sides ? formData.available_side_ids : [],
            free_sides_count: Math.max(0, parseInt(formData.free_sides_count) || 0),
            additional_side_price: parseFloat(formData.additional_side_price) || 15
          })
        });
      }

      toast.success(editingItem ? 'Item updated' : 'Item created');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              data-testid="item-name"
            />
          </div>
          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              data-testid="item-description"
            />
          </div>

          <div>
            <Label>Menu Type *</Label>
            <Select value={formData.menu_type} onValueChange={(v) => handleChange('menu_type', v)}>
              <SelectTrigger data-testid="menu-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {MENU_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span>{type.label}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Price (R) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                required
                data-testid="item-price"
              />
            </div>
            <div>
              <Label htmlFor="stock">Stock Quantity</Label>
              <Input
                id="stock"
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => handleChange('stock_quantity', e.target.value)}
                data-testid="item-stock"
              />
            </div>
          </div>

          {formData.menu_type === 'office_platters' && (
            <div>
              <Label htmlFor="min_serving">Minimum Serving Size</Label>
              <Input
                id="min_serving"
                type="number"
                value={formData.min_serving_size}
                onChange={(e) => handleChange('min_serving_size', parseInt(e.target.value) || 1)}
                min="1"
                data-testid="item-min-serving"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum number of servings per order</p>
            </div>
          )}

          {formData.menu_type !== 'canteen' && (
            <div className="flex items-center gap-2">
              <Switch
                id="loyalty"
                checked={formData.is_loyalty_eligible}
                onCheckedChange={(v) => handleChange('is_loyalty_eligible', v)}
              />
              <Label htmlFor="loyalty" className="text-sm">Counts towards loyalty discount</Label>
            </div>
          )}

          <div>
            <Label htmlFor="category">Category *</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              placeholder="e.g., DCP Package, Platter, Frozen"
              required
              data-testid="item-category"
            />
          </div>
          <div>
            <Label>Item Image</Label>
            <div className="mt-2 space-y-3">
              {(imagePreview || formData.image_url) && (
                <div className="relative w-full h-40 rounded-lg overflow-hidden bg-muted">
                  <img
                    src={imagePreview || formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="flex-1"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center">or paste image URL</div>
              <Input
                value={formData.image_url}
                onChange={(e) => {
                  handleChange('image_url', e.target.value);
                  setImagePreview(e.target.value || null);
                }}
                placeholder="https://..."
                data-testid="item-image"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="allergens">Allergens (comma-separated)</Label>
            <Input
              id="allergens"
              value={Array.isArray(formData.allergens) ? formData.allergens.join(', ') : formData.allergens}
              onChange={(e) => handleChange('allergens', e.target.value)}
              placeholder="e.g., Gluten, Dairy, Nuts"
              data-testid="item-allergens"
            />
          </div>

          {formData.menu_type === 'daily_meals' && (
            <div className="border rounded-xl p-4 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Sides Configuration</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Does this meal come with a side choice?</p>
                </div>
                <Switch
                  id="has_sides"
                  checked={formData.has_sides}
                  onCheckedChange={(v) => handleChange('has_sides', v)}
                  data-testid="item-has-sides"
                />
              </div>

              {formData.has_sides && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Free sides included</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.free_sides_count}
                        onChange={(e) => handleChange('free_sides_count', Math.max(0, parseInt(e.target.value) || 0))}
                        className="mt-1"
                        data-testid="item-free-sides"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">Set 0 = no free sides — customers can still add sides at the extra price.</p>
                    </div>
                    <div>
                      <Label className="text-xs">Extra side price (R)</Label>
                      <Input
                        type="number"
                        step="0.50"
                        min="0"
                        value={formData.additional_side_price}
                        onChange={(e) => handleChange('additional_side_price', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                        data-testid="item-extra-side-price"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs mb-2 block">Available sides (select which sides apply)</Label>
                    {availableSides.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No sides created yet. Go to Sides section to add sides first.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {availableSides.map(side => {
                          const isSelected = formData.available_side_ids.includes(side.side_id);
                          return (
                            <div
                              key={side.side_id}
                              onClick={() => {
                                const ids = formData.available_side_ids;
                                handleChange('available_side_ids',
                                  isSelected ? ids.filter(id => id !== side.side_id) : [...ids, side.side_id]
                                );
                              }}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm transition-colors ${
                                isSelected ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-white border border-border hover:bg-muted/50'
                              }`}
                              data-testid={`side-select-${side.side_id}`}
                            >
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                              }`}>
                                {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                              </div>
                              <span className="font-medium">{side.name}</span>
                              <span className="text-muted-foreground ml-auto">R{side.price?.toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex items-center space-x-2">
            <Switch
              id="vegetarian"
              checked={formData.is_vegetarian}
              onCheckedChange={(v) => handleChange('is_vegetarian', v)}
              data-testid="item-vegetarian"
            />
            <Label htmlFor="vegetarian">Vegetarian</Label>
          </div>
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary-600" data-testid="save-item-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MenuItemFormDialog;
